import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  getCurrentSeason,
  type Season,
} from '../shared/utils/getCurrentSeason';
import type { GenerateOutfitRequest, Outfit } from '../types/api';

/*
FOLLOWING CODE IS LEGACY AND SHOULD NOT BE REMOVED OR UPDATED!
DO NOT RELY ON THIS STORE FOR NEW FEATURES!
*/

/**
 * Store для параметров генерации образа
 * Хранит фильтры, которые применяются при POST запросе на генерацию
 */

interface GenerationFilters {
  // Основные параметры
  season: Season;
  gender: 'male' | 'female' | '';

  // Параметры из фильтров
  style?: string; // Выбранный стиль (enum key)
  colors?: string[]; // Цветовые палитры
  maxPrice?: number; // Максимальная цена
  isOnlyMadeInMoscow?: boolean; // Только вещи произведённые в Москве

  // Дополнительные параметры (для будущего использования)
  includeItems?: string[]; // UUIDs вещей которые должны быть включены
  excludeItems?: string[]; // UUIDs вещей которые должны быть исключены
}

interface GenerationStore {
  filters: GenerationFilters;
  pendingOutfitId: string | null; // ID образа который нужно загрузить вместо генерации

  // История сгенерированных образов
  outfitsHistory: any[]; // Последние 10 образов
  historyIndex: number; // Текущая позиция в истории (0 = самый новый, -1 = не в истории)

  // Actions
  setFilters: (filters: Partial<GenerationFilters>) => void;
  setSeason: (season: Season) => void;
  setGender: (gender: 'male' | 'female' | '') => void;
  setStyle: (style: string) => void;
  setColors: (colors: string[]) => void;
  setMaxPrice: (maxPrice: number) => void;
  resetFilters: () => void;
  setPendingOutfitId: (id: string | null) => void; // Установить ID образа для загрузки

  // History management
  addOutfitToHistory: (outfit: any) => void;
  goToPreviousOutfit: () => any | null;
  goToNextOutfit: () => any | null;
  canGoPrevious: () => boolean;
  canGoNext: () => boolean;
  updateOutfitInHistory: (outfitId: string, updates: Partial<Outfit>) => void;

  // Utility
  getGenerationParams: () => GenerateOutfitRequest;
}

const getDefaultFilters = (): GenerationFilters => ({
  season: getCurrentSeason(),
  gender: '',
  style: undefined,
  colors: undefined,
  maxPrice: undefined,
  isOnlyMadeInMoscow: undefined,
  includeItems: undefined,
  excludeItems: undefined,
});

export const useGenerationStore = create<GenerationStore>()(
  persist(
    (set, get) => ({
      filters: getDefaultFilters(),
      pendingOutfitId: null, // По умолчанию нет ожидающего образа
      outfitsHistory: [], // История образов
      historyIndex: -1, // -1 означает что мы не в режиме просмотра истории

      setFilters: newFilters => {
        set(state => ({
          filters: { ...state.filters, ...newFilters },
        }));
      },

      setSeason: season => {
        set(state => ({
          filters: { ...state.filters, season },
        }));
      },

      setGender: gender => {
        set(state => ({
          filters: { ...state.filters, gender },
        }));
      },

      setStyle: style => {
        set(state => ({
          filters: { ...state.filters, style },
        }));
      },

      setColors: colors => {
        set(state => ({
          filters: { ...state.filters, colors },
        }));
      },

      setMaxPrice: maxPrice => {
        set(state => ({
          filters: { ...state.filters, maxPrice },
        }));
      },

      resetFilters: () => {
        set({ filters: getDefaultFilters() });
      },

      setPendingOutfitId: id => {
        set({ pendingOutfitId: id });
      },

      // Добавить новый образ в историю
      addOutfitToHistory: outfit => {
        const { outfitsHistory, historyIndex } = get();

        // Если мы не в конце истории (просматривали старые), удаляем всё что после текущей позиции
        let newHistory =
          historyIndex >= 0
            ? outfitsHistory.slice(historyIndex)
            : [...outfitsHistory];

        // Добавляем новый образ в начало
        newHistory.unshift(outfit);

        // Ограничиваем историю 10 элементами
        if (newHistory.length > 10) {
          newHistory = newHistory.slice(0, 10);
        }

        set({
          outfitsHistory: newHistory,
          historyIndex: -1, // Сбрасываем индекс - мы на самом новом образе
        });
      },

      // Обновить образ в истории
      updateOutfitInHistory: (outfitId, updates) => {
        const { outfitsHistory } = get();
        const newHistory = outfitsHistory.map(outfit =>
          outfit.id === outfitId ? { ...outfit, ...updates } : outfit
        );
        set({ outfitsHistory: newHistory });
      },

      // Перейти к предыдущему образу в истории
      goToPreviousOutfit: () => {
        const { outfitsHistory, historyIndex } = get();

        // Если история пустая или мы уже в конце
        if (outfitsHistory.length === 0) return null;

        const currentIndex = historyIndex === -1 ? 0 : historyIndex;
        const nextIndex = currentIndex + 1;

        if (nextIndex >= outfitsHistory.length) {
          return null; // Достигли конца истории
        }

        set({ historyIndex: nextIndex });
        return outfitsHistory[nextIndex];
      },

      // Перейти к следующему образу в истории (вперёд)
      goToNextOutfit: () => {
        const { outfitsHistory, historyIndex } = get();

        if (historyIndex <= 0) {
          // Уже на самом новом или история пустая
          set({ historyIndex: -1 });
          return historyIndex === 0 ? outfitsHistory[0] : null;
        }

        const nextIndex = historyIndex - 1;
        set({ historyIndex: nextIndex === 0 ? -1 : nextIndex });
        return outfitsHistory[nextIndex];
      },

      // Можно ли перейти назад
      canGoPrevious: () => {
        const { outfitsHistory, historyIndex } = get();
        const currentIndex = historyIndex === -1 ? 0 : historyIndex;
        return (
          outfitsHistory.length > 0 && currentIndex < outfitsHistory.length - 1
        );
      },

      // Можно ли перейти вперёд
      canGoNext: () => {
        const { historyIndex } = get();
        return historyIndex > 0;
      },

      // Формирует параметры для API запроса генерации
      getGenerationParams: (): GenerateOutfitRequest => {
        const { filters } = get();
        // Формируем только те поля, которые определены
        const params: GenerateOutfitRequest = {};

        if (filters.style) params.style = filters.style;
        if (filters.colors && filters.colors.length > 0)
          params.colors = filters.colors;
        if (filters.maxPrice) params.maxPrice = filters.maxPrice;
        if (filters.isOnlyMadeInMoscow)
          params.isOnlyMadeInMoscow = filters.isOnlyMadeInMoscow;
        if (filters.includeItems && filters.includeItems.length > 0)
          params.includeItems = filters.includeItems;
        if (filters.excludeItems && filters.excludeItems.length > 0)
          params.excludeItems = filters.excludeItems;

        // Season и gender передаем всегда (даже если пустые)
        params.season = filters.season;
        params.gender = filters.gender;

        return params;
      },
    }),
    {
      name: 'generation-filters-storage',
      // Сохраняем только важные поля (pendingOutfitId и историю НЕ сохраняем - временные состояния)
      partialize: state => ({
        filters: {
          season: state.filters.season,
          gender: state.filters.gender,
          style: state.filters.style,
          colors: state.filters.colors,
          maxPrice: state.filters.maxPrice,
          isOnlyMadeInMoscow: state.filters.isOnlyMadeInMoscow,
        },
      }),
    }
  )
);
